import { useEffect, useState } from "react";
import { Smartphone, Share, Plus, X, Compass, MoreHorizontal } from "lucide-react";
import { usePwaInstall } from "@/lib/usePwaInstall";

/** Renders manual "Add to Home Screen" instructions on iOS.
 *  Detects iOS Safari vs Chrome/Firefox/Edge/Opera and shows the correct
 *  share-sheet path for each browser. Non-Safari iOS browsers also get an
 *  "Open in Safari" convenience button (universal link).
 */
const IOSInstallModal = () => {
  const [open, setOpen] = useState(false);
  const { iosBrowser } = usePwaInstall();

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("luxsova:show-ios-install", handler);
    return () => window.removeEventListener("luxsova:show-ios-install", handler);
  }, []);

  if (!open) return null;

  const isSafari = iosBrowser === "safari";

  const shareIconLocation = {
    safari: "at the bottom of Safari",
    chrome: "in the address bar (top-right)",
    firefox: "in the menu (⋯) at the bottom-right",
    edge: "in the menu (⋯) at the bottom",
    opera: "in the menu at the bottom",
  }[iosBrowser];

  const currentUrl = typeof window !== "undefined" ? window.location.href : "";
  // x-web-search:// works, but the cleanest way to force-open in Safari is to
  // use the safari:// scheme with a URL — this only works on iOS.
  const openInSafariHref = currentUrl.replace(/^https?:/, "");

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onClick={() => setOpen(false)}
      data-testid="install-ios-overlay"
    >
      <div
        className="bg-white w-full max-w-md p-6 sm:p-8 relative max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="install-ios-modal"
      >
        <button
          onClick={() => setOpen(false)}
          className="absolute top-3 right-3 p-1"
          aria-label="Close"
          data-testid="install-ios-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 bg-black text-white grid place-items-center">
            <Smartphone className="w-5 h-5" />
          </div>
          <div>
            <p className="overline text-neutral-500">Install on iPhone</p>
            <h3 className="font-serif-display text-2xl">
              Add Luxsova to Home Screen
            </h3>
          </div>
        </div>

        {!isSafari && (
          <div className="border border-amber-200 bg-amber-50 p-3 mb-5 text-sm">
            <p className="font-medium mb-1">
              You&apos;re using {iosBrowser === "chrome" ? "Chrome" : iosBrowser === "firefox" ? "Firefox" : iosBrowser === "edge" ? "Edge" : "another browser"} on iPhone.
            </p>
            <p className="text-neutral-700 text-[13px] leading-relaxed">
              For the smoothest install, open Luxsova in <b>Safari</b> and try
              again — or follow the steps below to install directly from{" "}
              {iosBrowser === "chrome"
                ? "Chrome"
                : iosBrowser === "firefox"
                ? "Firefox"
                : "your browser"}
              .
            </p>
            <a
              href={`x-safari-${openInSafariHref.startsWith("//") ? "https:" + openInSafariHref : currentUrl}`}
              className="mt-3 inline-flex items-center gap-2 bg-sky-600 text-white text-xs uppercase tracking-[0.18em] px-3 py-2"
              data-testid="install-open-in-safari"
            >
              <Compass className="w-4 h-4" /> Open in Safari
            </a>
          </div>
        )}

        <p className="text-sm text-neutral-600 mb-5">
          {isSafari
            ? "Safari doesn't support one-tap install. Just three quick steps:"
            : "Three quick steps to add the app to your home screen:"}
        </p>

        <ol className="space-y-4 text-sm">
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 bg-neutral-100 text-black grid place-items-center text-xs font-medium flex-shrink-0">
              1
            </span>
            <span className="flex-1 leading-relaxed">
              Tap the{" "}
              {isSafari ? (
                <Share className="w-4 h-4 inline -mt-0.5 text-sky-600" />
              ) : (
                <MoreHorizontal className="w-4 h-4 inline -mt-0.5" />
              )}{" "}
              <strong>{isSafari ? "Share" : "menu"}</strong> button{" "}
              {shareIconLocation}.
              {!isSafari && (
                <span className="text-neutral-500">
                  {" "}
                  Then tap <b>Share</b>.
                </span>
              )}
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 bg-neutral-100 text-black grid place-items-center text-xs font-medium flex-shrink-0">
              2
            </span>
            <span className="flex-1 leading-relaxed">
              Scroll down and tap{" "}
              <Plus className="w-4 h-4 inline -mt-0.5" />{" "}
              <strong>Add to Home Screen</strong>.
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 bg-neutral-100 text-black grid place-items-center text-xs font-medium flex-shrink-0">
              3
            </span>
            <span className="flex-1 leading-relaxed">
              Tap <strong>Add</strong> in the top-right corner. Luxsova will
              appear on your home screen like a native app.
            </span>
          </li>
        </ol>

        <button
          onClick={() => setOpen(false)}
          className="btn-ink w-full mt-7"
          data-testid="install-ios-got-it"
        >
          Got it
        </button>
      </div>
    </div>
  );
};

export default IOSInstallModal;
